import {
  VestingGraphData,
  VestingGraphPoint,
  VestingGraphPointGranularity,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { formatVestingCalendarDate } from './vesting-graph.format';
import { resolveAllocationPeriodVestingStepShares } from './vesting-graph.hover';
import { applyEqualColumnLayout, insertScheduleEventColumns } from './vesting-graph.layout';
import { buildMilestoneVestingGraphChartData } from './vesting-graph.milestone-series';
import {
  DateFormatter,
  MilestoneSegmentBound,
  UnvestedSummary,
  VestingGraphSeriesPoint,
} from './vesting-graph.types';

export const buildUnvestedSummary = (vestingGraph: VestingGraphData): UnvestedSummary => {
  const vestedShares = vestingGraph.points
    .filter((point) => point.status === VestingGraphPointStatus.VESTED)
    .reduce((sum, point) => sum + point.periodShares, 0);
  const unvestedShares = vestingGraph.points
    .filter((point) => point.status === VestingGraphPointStatus.UNVESTED)
    .reduce((sum, point) => sum + point.periodShares, 0);
  const expiredShares = vestingGraph.points
    .filter((point) => point.status === VestingGraphPointStatus.EXPIRED)
    .reduce((sum, point) => sum + point.periodShares, 0);
  const totalShares = vestedShares + unvestedShares + expiredShares;

  return {
    percent: totalShares > 0 ? (unvestedShares / totalShares) * 100 : 0,
    shares: unvestedShares,
  };
};

export const buildOriginalScheduleLookup = (originalSchedulePoints?: VestingGraphPoint[]) => {
  const lookup = new Map<number, number>();

  originalSchedulePoints?.forEach((point) => {
    lookup.set(new Date(point.date).getTime(), point.cumulativePercent);
  });

  return lookup;
};

/**
 * Boundary between the drawn-as-vested and drawn-as-projected halves of the curve.
 * Acceleration keeps unaccelerated bar heights and only marks later columns inactive, so
 * the split follows the last actually vested payment — not the acceleration date.
 */
export const resolveSplitTimestamp = (vestingGraph: VestingGraphData) => {
  const lastVestedPoint = [...vestingGraph.points]
    .reverse()
    .find((point) => point.status === VestingGraphPointStatus.VESTED);

  return lastVestedPoint
    ? new Date(lastVestedPoint.date).getTime()
    : new Date(vestingGraph.startDate).getTime();
};

const isProjectedStatus = (status: VestingGraphPointStatus) =>
  status === VestingGraphPointStatus.UNVESTED || status === VestingGraphPointStatus.EXPIRED;

const buildSeriesPoint = ({
  format,
  originalScheduleLookup,
  point,
  vestingStepShares,
}: {
  format: DateFormatter;
  originalScheduleLookup: Map<number, number>;
  point: VestingGraphPoint;
  vestingStepShares: number;
}): VestingGraphSeriesPoint => {
  const timestamp = new Date(point.date).getTime();
  const { cumulativePercent } = point;
  const isProjected = isProjectedStatus(point.status);

  const originalSchedulePercent = originalScheduleLookup.get(timestamp);

  return {
    calendarTimestamp: timestamp,
    cumulativePercent,
    cumulativeShares: point.cumulativeShares,
    dateLabel: formatVestingCalendarDate(format, point.date),
    expiredPercent: null,
    milestoneName: point.milestoneName,
    monthsToComplete: point.monthsToComplete,
    // 0% would stroke the X-axis itself (the Area uses a dashed stroke), so the baseline
    // stays a solid rule until the unaccelerated schedule actually leaves the floor.
    originalSchedulePercent:
      originalSchedulePercent === undefined || originalSchedulePercent === 0
        ? null
        : originalSchedulePercent,
    periodShares: point.periodShares,
    // Status is the source of truth: acceleration keeps unaccelerated heights and only
    // flips later columns to UNVESTED, so the area split follows those statuses — not a
    // forced jump to 100% at the acceleration date. Post-expiry payments use the same
    // projected wash as unvested columns.
    projectedPercent: isProjected ? cumulativePercent : null,
    slotHalfWidth: 0,
    status: point.status,
    timestamp,
    vestedPercent: isProjected ? null : cumulativePercent,
    vestingStepShares,
  };
};

/**
 * Turns the API point series into Recharts data for every vesting type. The API only
 * reports vesting events, so the curve is anchored with a 0% point at the grant date and,
 * when full vesting is later than the last payment, plateaued to that date without inventing
 * a higher cumulative percent or share count.
 */
export const buildVestingGraphChartData = ({
  format,
  vestingGraph,
}: {
  format: DateFormatter;
  vestingGraph: VestingGraphData;
}) => {
  if (vestingGraph.granularity === VestingGraphPointGranularity.MILESTONE) {
    return buildMilestoneVestingGraphChartData({ format, vestingGraph });
  }

  const calendarDomainMin = new Date(vestingGraph.startDate).getTime();
  const calendarDomainMax = new Date(
    vestingGraph.fullVestingDate ?? vestingGraph.points.at(-1)?.date ?? vestingGraph.startDate,
  ).getTime();
  const originalScheduleLookup = buildOriginalScheduleLookup(vestingGraph.originalSchedulePoints);

  let vestingStepShares = 0;
  const chartData: VestingGraphSeriesPoint[] = [
    {
      calendarTimestamp: calendarDomainMin,
      cumulativePercent: 0,
      cumulativeShares: 0,
      dateLabel: formatVestingCalendarDate(format, vestingGraph.startDate),
      expiredPercent: null,
      isGrantStart: true,
      monthsToComplete: 0,
      originalSchedulePercent: null,
      periodShares: 0,
      projectedPercent: null,
      slotHalfWidth: 0,
      status: VestingGraphPointStatus.VESTED,
      timestamp: calendarDomainMin,
      vestedPercent: 0,
      vestingStepShares: 0,
    },
    ...vestingGraph.points.map((point) => {
      if (point.periodShares > 0) vestingStepShares = point.periodShares;

      const allocationPeriodShares = resolveAllocationPeriodVestingStepShares({
        points: vestingGraph.points,
        pointTimestamp: new Date(point.date).getTime(),
        vestingPeriods: vestingGraph.vestingPeriods,
      });

      return buildSeriesPoint({
        format,
        originalScheduleLookup,
        point,
        vestingStepShares: allocationPeriodShares ?? vestingStepShares,
      });
    }),
  ];

  const lastPoint = chartData.at(-1)!;

  // Only extend forward: a full-vest date after the last payment becomes a plateau of that
  // holding. Acceleration often moves fullVestingDate earlier than the original schedule —
  // appending then would park a leftover 100% bar past the last labelled column.
  if (vestingGraph.fullVestingDate && calendarDomainMax > lastPoint.calendarTimestamp) {
    const isProjected = isProjectedStatus(lastPoint.status);

    chartData.push({
      ...lastPoint,
      calendarTimestamp: calendarDomainMax,
      dateLabel: formatVestingCalendarDate(format, vestingGraph.fullVestingDate),
      expiredPercent: null,
      periodShares: 0,
      projectedPercent: isProjected ? lastPoint.cumulativePercent : null,
      timestamp: calendarDomainMax,
      vestedPercent: isProjected ? null : lastPoint.cumulativePercent,
      vestingStepShares: lastPoint.vestingStepShares,
    });
  }

  // Acceleration / expiry are real columns on their calendar dates — inserted before the
  // layout remap so markers and axis ticks land where the date actually is.
  const chartDataWithEvents = insertScheduleEventColumns({
    chartData,
    format,
    vestingGraph,
  });

  // Time-based charts (standard and dynamic) keep equal payment columns. Dates are
  // labels, not a timeline. The cliff is an empty leading span with a minimum width.
  const layoutChartData = applyEqualColumnLayout(chartDataWithEvents, {
    cliffMonths: vestingGraph.cliff?.months,
  });

  return {
    ...layoutChartData,
    milestoneSegmentBounds: [] as MilestoneSegmentBound[],
  };
};
